import { createFileRoute, Link } from "@tanstack/react-router";
import {
  CalendarCheck,
  CarFront,
  CheckCircle2,
  Headphones,
  MapPinned,
  ShieldCheck,
  Sparkles,
  Zap,
} from "lucide-react";

import heroCar960 from "@/assets/hero-car-960.webp";
import heroCar1920 from "@/assets/hero-car-1920.webp";
import { CarGrid } from "@/components/site/CarGrid";
import { FaqAccordion, faqs } from "@/components/site/FaqAccordion";
import { NewsletterForm } from "@/components/site/NewsletterForm";
import { PromoCarsCarousel } from "@/components/site/PromoCarsCarousel";
import { PromotionsCarousel } from "@/components/site/PromotionsCarousel";
import { SearchWidget } from "@/components/site/SearchWidget";

import { Button } from "@/components/ui/button";
import { cars } from "@/data/cars";
import { GOOGLE_MAPS_PROFILE, hreflangLinks } from "@/lib/i18n";
import { publicPageHeaders } from "@/lib/http-cache";

const title = "Car Rental Bucharest — Day Rent Now, No Deposit";
const description =
  "Car rental Bucharest with no deposit, no credit card. Day rent now from €44/day, free Otopeni Airport pickup, insurance and rovinieta included.";

export const Route = createFileRoute("/")({
  headers: publicPageHeaders,
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:url", content: "https://carentals.ro/" },
      {
        name: "keywords",
        content:
          "car rental no deposit romania, car rental bucharest, no credit card car rental bucharest, rent a car without deposit romania, otopeni airport car rental",
      },
    ],
    links: [
      { rel: "canonical", href: "https://carentals.ro/" },
      {
        rel: "preload",
        as: "image",
        href: heroCar960,
        imageSrcSet: `${heroCar960} 960w, ${heroCar1920} 1920w`,
        imageSizes: "100vw",
        fetchPriority: "high",
      },
      ...hreflangLinks("/", "/ro"),
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AutoRental",
          "@id": "https://carentals.ro/#localbusiness",
          name: "Car Rental Bucharest",
          url: "https://carentals.ro",
          image: "https://carentals.ro/icon-512.png",
          address: {
            "@type": "PostalAddress",
            streetAddress: "Bulevardul Regina Elisabeta 33",
            addressLocality: "Bucharest",
            addressCountry: "RO",
          },
          sameAs: [GOOGLE_MAPS_PROFILE],
          hasMap: GOOGLE_MAPS_PROFILE,
          priceRange: "€€",
          areaServed: { "@type": "City", name: "Bucharest", addressCountry: "RO" },
          paymentAccepted: "Debit card, Cash, Bank transfer",
          currenciesAccepted: "EUR, RON",
          slogan: "Car rental in Bucharest with no deposit and no credit card",
          makesOffer: {
            "@type": "Offer",
            name: "No deposit car rental in Bucharest",
            priceCurrency: "EUR",
            price: "44",
            availability: "https://schema.org/InStock",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((faq) => ({
            "@type": "Question",
            name: faq.q,
            acceptedAnswer: { "@type": "Answer", text: faq.a },
          })),
        }),
      },
    ],
  }),
  component: Index,
});

const noDepositPoints = [
  {
    icon: ShieldCheck,
    title: "€0 security deposit",
    text: "No franchise hold and no cash guarantee — nothing is frozen before, during or after your rental.",
  },
  {
    icon: Sparkles,
    title: "No credit card needed",
    text: "Pay by debit card, cash in EUR or RON, or bank transfer for company and long-term rentals.",
  },
  {
    icon: CheckCircle2,
    title: "Everything included",
    text: "Full CASCO and RCA insurance plus the rovinieta road vignette are part of the daily rate.",
  },
  {
    icon: MapPinned,
    title: "Free delivery",
    text: "Free handover in Bucharest and at Henri Coandă (Otopeni) Airport, day or night.",
  },
];

const steps = [
  {
    icon: MapPinned,
    title: "Choose Locations",
    text: "Pick the date and location for your rental, considering your itinerary, pick-up and drop-off points (airport, city center) and rental duration.",
  },
  {
    icon: CarFront,
    title: "Pick-Up Locations",
    text: "Check availability of your desired vehicle type for your chosen dates and location, along with rental rates, taxes, fees and any additional charges.",
  },
  {
    icon: CalendarCheck,
    title: "Book your Car",
    text: "Proceed to make the reservation by providing the required details, such as your driver's license and payment details.",
  },
];

const benefits = [
  {
    icon: Zap,
    title: "Easy & Fast Booking",
    text: "A fast, streamlined booking process built around customer service — reserve your car in under two minutes.",
  },
  {
    icon: MapPinned,
    title: "Many Pickup Locations",
    text: "Collect from our office on Bulevardul Regina Elisabeta, from the airport, or have the car delivered anywhere in Bucharest.",
  },
  {
    icon: Headphones,
    title: "Customer Satisfaction",
    text: "A customer-centric experience from first enquiry to return, with support in English and Romanian at every step.",
  },
];

function Index() {
  const recommended = cars.slice(0, 8);
  const promoCars = cars.filter((car) => car.pricePerDay >= 60).slice(0, 6);


  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden bg-ink text-ink-foreground">
        <img
          src={heroCar960}
          srcSet={`${heroCar960} 960w, ${heroCar1920} 1920w`}
          sizes="100vw"
          alt="Luxury black SUV parked on a Bucharest boulevard at golden hour"
          width={1920}
          height={1088}
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 size-full object-cover opacity-55"
        />

        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/80 to-ink/20" />
        <div className="container-page relative py-20 lg:py-32">
          <p className="eyebrow">No Deposit · No Credit Card · Bucharest, Romania</p>
          <h1 className="mt-4 max-w-3xl text-4xl font-extrabold leading-[1.05] sm:text-5xl lg:text-6xl">
            Car Rental in Bucharest with No Deposit
          </h1>
          <p className="mt-5 max-w-2xl text-base text-ink-foreground/80 sm:text-lg">
            Rent a premium or luxury car in Bucharest without a security deposit and without a
            credit card. Nothing is blocked on your card — full insurance and the rovinieta are
            included, and delivery in the city and at Otopeni Airport is free.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Button asChild variant="gold" size="lg">
              <Link to="/rentals">Rent Now</Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-white/30 bg-transparent text-ink-foreground hover:bg-white/10 hover:text-ink-foreground"
            >
              <a href="https://wa.me/40738792499" target="_blank" rel="noreferrer">WhatsApp us</a>
            </Button>
          </div>

          <SearchWidget className="mt-12 text-foreground" />

          <ul className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-sm text-ink-foreground/80">
            {[
              "€0 security deposit",
              "No credit card required",
              "Full insurance & rovinieta included",
              "Free delivery in Bucharest & at Otopeni",
            ].map(
              (item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckCircle2 className="size-4 text-gold" /> {item}
                </li>
              ),
            )}
          </ul>
        </div>
      </section>

      {/* No deposit / no credit card */}
      <section className="defer-render container-page py-20">
        <div className="max-w-3xl">
          <p className="eyebrow">Why carentals.ro</p>
          <h2 className="mt-3 text-3xl font-bold sm:text-4xl">
            No deposit, no credit card — car rental in Romania without the hold
          </h2>
          <p className="mt-4 text-muted-foreground">
            Most rental companies in Bucharest freeze €300 to €1,500 on a credit card and release it
            weeks after you return the car. We do not. carentals.ro takes no security deposit and
            accepts debit cards, cash in EUR or RON, or bank transfer — so your travel budget stays
            in your account.
          </p>
        </div>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {noDepositPoints.map((point) => (
            <div key={point.title} className="rounded-xl border border-border bg-card p-6 shadow-card">
              <point.icon className="size-6 text-gold" />
              <h3 className="mt-4 font-display text-lg font-bold">{point.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{point.text}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 flex flex-wrap gap-3">
          <Button asChild variant="gold">
            <Link to="/car-rental-no-deposit-bucharest">How no-deposit rental works</Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/rent-a-car-without-credit-card-bucharest">Renting without a credit card</Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/good-to-know">Good to know in Bucharest</Link>
          </Button>
        </div>
        <p className="mt-6 text-sm text-muted-foreground">
          Read more:{" "}
          <Link
            to="/blog/$slug"
            params={{ slug: "car-rental-no-deposit-romania" }}
            className="font-semibold text-gold hover:underline"
          >
            car rental with no deposit in Romania
          </Link>
          ,{" "}
          <Link
            to="/blog/$slug"
            params={{ slug: "rent-a-car-without-credit-card-bucharest" }}
            className="font-semibold text-gold hover:underline"
          >
            renting without a credit card
          </Link>{" "}
          and{" "}
          <Link
            to="/blog/$slug"
            params={{ slug: "cheap-car-rental-bucharest-otopeni-airport" }}
            className="font-semibold text-gold hover:underline"
          >
            cheap rentals at Otopeni Airport
          </Link>
          .
        </p>

      </section>

      {/* Promotions */}
      <section className="defer-render container-page py-20">
        <div className="grid gap-4 sm:flex sm:items-end sm:justify-between">
          <div className="max-w-xl">
            <p className="eyebrow">Promotions</p>
            <h2 className="mt-3 text-3xl font-bold sm:text-4xl">Current offers</h2>
            <p className="mt-3 text-muted-foreground">
              Seasonal deals on the fleet — free delivery, longer-stay discounts and weekend
              upgrades.
            </p>
          </div>
        </div>
        <div className="mt-10">
          <PromotionsCarousel />
        </div>

        <h3 className="mt-16 font-display text-2xl font-bold">Cars on promotion</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          15% off the daily rate this month on these models.
        </p>
        <div className="mt-8">
          <PromoCarsCarousel cars={promoCars} />
        </div>
      </section>

      {/* Recommended cars */}
      <section className="defer-render bg-secondary/60 py-20">
        <div className="container-page">
          <div className="grid gap-4 sm:flex sm:items-end sm:justify-between">
            <div className="max-w-xl">
              <p className="eyebrow">Our fleet</p>
              <h2 className="mt-3 text-3xl font-bold sm:text-4xl">Recommended Cars</h2>
              <p className="mt-3 text-muted-foreground">
                Hand-picked premium models, fully serviced and ready for pickup today.
              </p>
            </div>
            <Button asChild variant="outline">
              <Link to="/rentals">View all 28 cars</Link>
            </Button>
          </div>
          <CarGrid cars={recommended} className="mt-10" />
        </div>
      </section>

      {/* How it works */}
      <section className="defer-render container-page py-20">
        <div className="max-w-2xl">
          <p className="eyebrow">How it works</p>
          <h2 className="mt-3 text-3xl font-bold sm:text-4xl">Rent in three simple steps</h2>
        </div>
        <ol className="mt-10 grid gap-6 md:grid-cols-3">
          {steps.map((step, i) => (
            <li
              key={step.title}
              className="rounded-2xl border border-border bg-card p-7 shadow-card"
            >
              <div className="flex items-center gap-3">
                <span className="grid size-11 place-items-center rounded-xl bg-accent text-accent-foreground">
                  <step.icon className="size-5" />
                </span>
                <span className="font-display text-sm font-bold text-gold">
                  0{i + 1}
                </span>
              </div>
              <h3 className="mt-5 font-display text-lg font-bold">{step.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{step.text}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* Why choose us */}
      <section className="defer-render bg-ink py-20 text-ink-foreground">
        <div className="container-page">
          <div className="max-w-2xl">
            <p className="eyebrow">Why choose us</p>
            <h2 className="mt-3 text-3xl font-bold sm:text-4xl">
              Premium service, without the premium hassle
            </h2>
          </div>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {benefits.map((benefit) => (
              <div key={benefit.title} className="rounded-2xl border border-white/10 bg-white/5 p-7">
                <span className="grid size-11 place-items-center rounded-xl bg-gold text-gold-foreground">
                  <benefit.icon className="size-5" />
                </span>
                <h3 className="mt-5 font-display text-lg font-bold">{benefit.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-ink-foreground/70">
                  {benefit.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* In-depth guide */}
      <section className="defer-render container-page py-20">
        <div className="max-w-3xl">
          <p className="eyebrow">The full picture</p>
          <h2 className="mt-3 text-3xl font-bold sm:text-4xl">
            Car rental in Bucharest, explained end to end
          </h2>
          <p className="mt-4 text-muted-foreground">
            Renting a car should take minutes, not an afternoon at a counter. Below is everything
            that happens between the moment you day rent now on this page and the moment you hand
            the keys back — the paperwork, the pickup at Otopeni Airport, our deposit and credit
            card policy, and what the daily rate actually covers.
          </p>
        </div>

        <div className="mt-12 grid gap-10 lg:grid-cols-2">
          <article>
            <h3 className="font-display text-xl font-bold">The rental process, step by step</h3>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              Start by choosing your dates and pickup point in the search box at the top of this
              page. The fleet list updates instantly with the 28 cars available for that window, so
              you never reserve a model that is already out on a rental. Every car card shows the
              real daily rate for your selected duration — a one day rent is priced differently from
              a seven day rent, and the discount is applied automatically rather than hidden behind
              a promo code.
            </p>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              Once you pick a car, the booking form asks for your name, contact details, the pickup
              and drop-off locations, and anything else we should know: a child seat, an extra
              driver, a late-night arrival, or a planned trip into Transylvania. You will receive an
              email confirmation within minutes, and a member of our team reads every request
              personally. There is no call centre, no queue and no upsell script.
            </p>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              The day before your rental starts, we send a preparation notice. On the morning of
              pickup you get a second message with the exact meeting point, the plate number and the
              phone of the colleague who will meet you. Signing the contract takes about five
              minutes: we check your licence and ID, walk around the car together, note the fuel
              level and mileage, and photograph the vehicle so both sides have the same record.
            </p>
          </article>

          <article>
            <h3 className="font-display text-xl font-bold">
              Otopeni Airport pickup and drop-off
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              Henri Coandă International Airport (OTP) in Otopeni is our busiest handover point, and
              delivery there is free — there is no airport surcharge, no shuttle bus to an off-site
              depot and no minimum rental length. We track your flight number, so if you land two
              hours late the car and the driver simply wait. Handovers run 24 hours a day, including
              on public holidays and at 04:00 arrivals.
            </p>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              After you clear customs, walk out to the arrivals hall and look for your name on a
              board; you will typically be behind the wheel within ten minutes of leaving baggage
              claim. Returning is the same process in reverse: park at the departures kerb, hand the
              keys over, and we complete the return inspection while you head to check-in. If your
              return flight is early, we can also collect the car from your hotel in Bucharest the
              evening before.
            </p>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              From Otopeni, the A3 motorway takes you into central Bucharest in roughly 30 minutes,
              Brașov and the Carpathians are about two and a half hours away, and the Black Sea
              coast at Constanța is under three hours on the A2. Every car leaves with a valid
              rovinieta road vignette, so you can join the motorway immediately without stopping to
              buy one.
            </p>
          </article>

          <article>
            <h3 className="font-display text-xl font-bold">
              Our deposit and credit card policy, in plain words
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              A traditional rental company protects itself by freezing a large sum on your credit
              card — usually between €300 and €1,500, sometimes far more on premium models. That
              money is unavailable to you for the length of the trip and often for several weeks
              afterwards while the release works its way through your bank. It is the single most
              common complaint travellers have about renting in Romania.
            </p>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              We removed that step entirely. There is no security deposit, no franchise hold and no
              cash guarantee. Because nothing is blocked, you do not need a credit card at all: pay
              by debit card, in cash in EUR or RON, or by bank transfer if you are booking through a
              company. Younger drivers and travellers whose bank does not issue credit cards can
              rent on exactly the same terms as everyone else.
            </p>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              In exchange we ask for something simple: a driving licence held for at least one year,
              a valid ID or passport, a minimum age of 21, and that the car comes back in the state
              it left in. Full CASCO and RCA insurance is already included in the daily rate, so
              ordinary wear, and the vast majority of incidents, are covered without any extra
              product being sold to you at the counter.
            </p>
          </article>

          <article>
            <h3 className="font-display text-xl font-bold">Day rentals and longer stays</h3>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              A single day rent is perfectly normal here. Plenty of our customers book a car for one
              day to reach a meeting in Ploiești, to visit Peleș Castle in Sinaia, or to move
              something across the city. There is no weekend minimum and no penalty for short
              bookings — you pay the day rate, collect the car and return it the same evening if
              that is all you need.
            </p>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              From three days upward the daily rate starts dropping, and it drops again at seven
              days and at one month. The booking form tells you exactly how much you would save by
              extending, so you can see whether adding a day makes your trip cheaper overall. Monthly
              and corporate contracts, including replacement vehicles for company fleets, are quoted
              individually by email.
            </p>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              Mileage is unlimited inside Romania on every booking, so a weekend loop through
              Transfăgărășan, Sibiu and Brașov costs the same as staying inside Bucharest. Travel to
              neighbouring EU countries is possible with prior written approval — tell us your route
              when you book and we prepare the green card documents in advance.
            </p>
          </article>
        </div>

        <div className="mt-12 rounded-3xl border border-border bg-card p-8 shadow-card">
          <h3 className="font-display text-xl font-bold">What renting with carentals.ro includes</h3>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-muted-foreground">
            Our fleet is made up of 28 well-maintained premium and economy cars, from a compact
            Ford Fiesta for city driving to executive BMW and Mercedes sedans and seven-seat SUVs
            for family trips. Every vehicle is serviced on schedule, cleaned and disinfected between
            rentals, and fitted with winter tyres in the cold season as Romanian law requires.
          </p>
          <ul className="mt-6 grid gap-3 text-sm text-muted-foreground sm:grid-cols-2">
            {[
              "Full CASCO and RCA insurance in the daily rate",
              "Rovinieta road vignette already valid",
              "Unlimited mileage inside Romania",
              "24/7 roadside assistance across the country",
              "Free delivery in Bucharest and at Otopeni Airport",
              "No deposit, no credit card, no hidden counter fees",
              "Support in English and Romanian, by phone or WhatsApp",
              "Child seats and additional drivers on request",
            ].map((item) => (
              <li key={item} className="flex items-start gap-2">
                <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-gold" />
                {item}
              </li>
            ))}
          </ul>
          <p className="mt-6 max-w-3xl text-sm leading-relaxed text-muted-foreground">
            We are a local Bucharest company, not a franchise of an international chain, and our
            office on Bulevardul Regina Elisabeta 33 is a five-minute walk from Cișmigiu Gardens. If
            something goes wrong on the road you speak to the same small team that handed you the
            keys, in your own language, at any hour. That is the difference we are trying to make:
            premium cars, honest pricing and a human on the other end of the phone.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="defer-render container-page py-20">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
          <div>
            <p className="eyebrow">FAQ</p>
            <h2 className="mt-3 text-3xl font-bold sm:text-4xl">Frequently asked questions</h2>
            <p className="mt-3 text-sm text-muted-foreground">
              Still have questions? Write to{" "}
              <a href="mailto:book@carentals.ro" className="font-semibold text-gold">
                book@carentals.ro
              </a>
              .
            </p>
            <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground">
              <ShieldCheck className="size-4 text-gold" /> Fully insured, GDPR compliant
            </div>
            <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
              <Sparkles className="size-4 text-gold" /> Book longer, save more
            </div>
          </div>
          <FaqAccordion />
        </div>
      </section>

      <section className="defer-render container-page pb-8">
        <div className="rounded-3xl border border-gold/25 bg-card p-8 shadow-lift sm:p-12">
          <div className="grid items-center gap-8 md:grid-cols-2">
            <div>
              <p className="eyebrow text-gold">Stay in the loop</p>
              <h2 className="mt-3 font-display text-2xl font-bold sm:text-3xl">
                Private offers for our members
              </h2>
              <p className="mt-3 text-sm text-muted-foreground">
                Seasonal rates, new arrivals in the fleet and long-term discounts — one short email
                a month, never shared with anyone.
              </p>
            </div>
            <NewsletterForm variant="card" />
          </div>
        </div>
      </section>
    </>

  );
}
